"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useCurrency } from "@/stores/currency-store";
import { useAuth } from "@/components/auth/auth-provider";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRight,
  User,
  Building2,
  Calendar,
  Banknote,
  FileText,
  ImageIcon,
  Save,
  X,
  Check,
  ChevronsUpDown,
  Search,
  Loader2,
  Gift,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  // Eye, // ซ่อนไว้ก่อน (ยังไม่ใช้)
  Smartphone,
} from "lucide-react";
import { SaveConfirmDialog } from "@/components/ui/confirm-dialog";
import { getUserService, type Member } from "@/domain/user";
import { BankService } from "@/domain/bank";
import {
  TransactionService,
  type CreateTransactionData,
} from "@/domain/transaction";
import { toast } from "sonner";
import { BankLogo } from "@/components/ui/bank-logo";
import { ThaiDatePicker, TimePicker24 } from "@/components/ui/date-time-picker";
import { toDateInputValue, toTimeInputValue } from "@/lib/date-format";

// ============================================
// Types
// ============================================

export type TransactionDialogMode =
  | "create-deposit"
  | "create-withdraw"
  | "view"
  | "edit";

export interface TransactionFormData {
  type: "Deposit" | "Withdraw";
  isBonus: boolean;
  webbankaccountId: string | null;
  memberId: string;
  memberName: string;
  memberUsername: string;
  dateTime: string;
  amount: number;
  status:
    | "Pending"
    | "Processing"
    | "Approved"
    | "Rejected"
    | "Completed"
    | "Cancelled";
  notes: string;
  image: File | string | null;
  withdrawBy?: "Auto" | "Manual" | "Manaul"; // 'Manaul' = legacy typo
  promotionCode?: string;
}

export interface WebBankAccount {
  id: string;
  bankName: string;
  bankAccount: string;
  accountName: string;
  imageUrl: string;
  amount: number;
  type: "deposit" | "withdraw";
  isBoth?: boolean;
  primaryDeposit?: boolean;
  primaryWithdraw?: boolean;
  gatewayApi?: boolean; // ✅ Gateway API capability
  bankId?: string; // Bank ID (e.g., 'truewallet', 'kbank')
  banklistSid?: number; // Bank list ID (19 = TrueWallet)
}

export interface MemberOption {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  phone: string;
  bankAccount: string;
  bankName: string;
  bankType?: number; // 1 = Regular bank, 2 = TrueWallet
  agentStatus: "ACTIVE" | "SUSPEND";
}

export interface Transaction {
  id: string;
  type: "Deposit" | "Withdraw";
  createFrom?: "Panel" | "Member" | "Transfer" | null;
  isBonus?: boolean;
  memberId?: string;
  memberUsername?: string;
  memberName?: string;
  memberBankAccount?: string;
  memberBankName?: string;
  memberAgentStatus?: string;
  webBankId?: string;
  webBankAccount?: string;
  webBankName?: string;
  webBankAccountName?: string;
  amount?: number;
  status?:
    | "Pending"
    | "Processing"
    | "Approved"
    | "Rejected"
    | "Completed"
    | "Cancelled";
  notes?: string;
  statusNote?: string;
  promotionCode?: string;
  dateTime?: string;
  datetimeCreated?: string;
  image?: string;
  withdrawBy?: string;
  isCreditWithdraw?: string;
  isCashWithdraw?: string;
  isCreditWithdrawAmount?: number;
  isCashWithdrawAmount?: number;
  uuid?: string;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

interface TransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: TransactionDialogMode;
  transaction?: Transaction | null;
  onSave?: (data: TransactionFormData) => Promise<void>;
}

// ============================================
// Web Bank Account Loading
// ============================================

// Note: Bank accounts are now loaded from API via BankService

// ============================================
// Helper Functions
// ============================================

/**
 * Get full URL for slip image
 * Runtime config takes precedence over build-time config
 */
function getSlipImageUrl(imagePath?: string): string | null {
  if (!imagePath) return null;

  // Cloud storage (Spaces/S3): already a full URL
  if (imagePath.startsWith('http')) return imagePath;

  // Normalize old format to new location
  // Old format: "uploads/1771420494750.jpg" → "uploads/slips-old/1771420494750.jpg"
  // New format: "uploads/slips/slip-2026-02-18-xxx.jpg" → keep as-is
  let normalizedPath = imagePath;

  if (normalizedPath.startsWith('uploads/') && !normalizedPath.includes('slips/')) {
    // Old format detected - map to slips-old directory
    normalizedPath = normalizedPath.replace('uploads/', 'uploads/slips-old/');
  }

  // Try runtime config first (from public/config.js)
  const runtimeConfig = (window as any).APP_CONFIG;
  if (runtimeConfig?.PARSE_V2_SERVER_URL) {
    const baseUrl = runtimeConfig.PARSE_V2_SERVER_URL.replace(/\/api\/v1$/, '');
    return `${baseUrl}/${normalizedPath}`;
  }

  // Fallback to build-time config
  const buildTimeUrl =
    import.meta.env.VITE_FRONT_API_URL ||
    import.meta.env.VITE_PARSE_V2_SERVER_URL?.replace(/\/api\/v[12]$/, '') ||
    '';
  return `${buildTimeUrl}/${normalizedPath}`;
}

// ============================================
// Component
// ============================================

export function TransactionDialog({
  open,
  onOpenChange,
  mode,
  transaction,
  onSave,
}: TransactionDialogProps) {
  const { format: formatCurrency } = useCurrency();
  const { sessionToken } = useAuth();

  // Form State
  const [isBonus, setIsBonus] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState<string>("");
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(
    null
  );
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [isSearchingMembers, setIsSearchingMembers] = useState(false);
  const [dateTime, setDateTime] = useState("");
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState<
    | "Pending"
    | "Processing"
    | "Approved"
    | "Rejected"
    | "Completed"
    | "Cancelled"
  >("Pending");
  const [notes, setNotes] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [promotionCode, setPromotionCode] = useState("");

  // Promotion Info State (สำหรับแสดงข้อมูลโปรโมชัน)
  const [memberPromotionInfo, setMemberPromotionInfo] = useState<{
    code?: string;
    max_withdraw?: number;
    hasPromotion: boolean;
  } | null>(null);

  // Bank Accounts State
  const [bankAccounts, setBankAccounts] = useState<WebBankAccount[]>([]);
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);

  // UI State
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const isDeposit =
    mode === "create-deposit" || transaction?.type === "Deposit";
  const isWithdraw =
    mode === "create-withdraw" || transaction?.type === "Withdraw";
  const isViewMode = mode === "view";
  const isCreateMode = mode === "create-deposit" || mode === "create-withdraw";
  const isEditMode = mode === "edit";

  // ✅ เช็คว่าเป็นรายการที่ "ไม่สามารถระบุได้" หรือไม่
  const isUnidentifiedMember =
    transaction?.memberName?.includes("ไม่สามารถ") ||
    transaction?.memberName?.includes("ไม่สามารถระบุได้") ||
    transaction?.memberName?.trim() === "" ||
    !transaction?.memberId;

  // ✅ Lock เมื่อ: view mode, หรือ transaction ถูกสร้างแล้ว (ไม่ใช่ create mode)
  // ✅ แต่ถ้าเป็นรายการที่ "ไม่สามารถระบุได้" ใน edit mode → อนุญาตให้เลือก member ใหม่ได้
  const isLocked: boolean =
    isViewMode || (!!transaction && !isCreateMode && !isUnidentifiedMember);

  // ✅ สำหรับรายการฝากที่ "ไม่สามารถระบุได้" → lock ฝากโบนัส, ธนาคาร, วันเวลา, จำนวนเงิน (แต่ member และ notes แก้ได้)
  const isUnidentifiedDepositEdit: boolean = !!(
    isDeposit &&
    isEditMode &&
    transaction &&
    isUnidentifiedMember
  );

  // ✅ Filter bank accounts based on type (รองรับ Both ด้วย)
  // - ฝาก: แสดงบัญชีที่มี type = "deposit" หรือ isBoth = true
  // - ถอน: แสดงบัญชีที่มี type = "withdraw" หรือ isBoth = true (แสดงทั้งหมด ไม่กรอง gatewayApi)
  // ✅ Sort: regular banks (banklistSid ≠ 19) ขึ้นก่อน TrueWallet (banklistSid = 19) เสมอ
  const availableBanks = bankAccounts.filter((b) => {
    if (isDeposit) {
      // ✅ ฝาก: แสดงบัญชีฝากและ both
      const matches = b.type === "deposit" || b.isBoth === true;
      return matches;
    } else {
      // ✅ ถอน: แสดงบัญชีถอนทั้งหมด (withdraw หรือ both) - ไม่กรอง gatewayApi
      const matches = b.type === "withdraw" || b.isBoth === true;
      return matches;
    }
  }).sort((a, b) => {
    const aIsTW = a.banklistSid === 19;
    const bIsTW = b.banklistSid === 19;
    if (aIsTW !== bIsTW) return aIsTW ? 1 : -1;
    return 0;
  });

  // ✅ Debug: Log filtered banks
  useEffect(() => {
    if (open && bankAccounts.length > 0) {
      console.log("🏦 [TransactionDialog] Filter debug:", {
        isDeposit,
        totalBanks: bankAccounts.length,
        availableBanks: availableBanks.length,
        bankDetails: bankAccounts.map((b) => ({
          name: b.bankName,
          type: b.type,
          isBoth: b.isBoth,
          primaryWithdraw: b.primaryWithdraw,
        })),
      });
    }
  }, [open, isDeposit, bankAccounts, availableBanks]);

  // Load bank accounts from API
  useEffect(() => {
    const loadBankAccounts = async () => {
      if (!open) return;

      setIsLoadingBanks(true);
      try {
        const token =
          sessionToken ||
          sessionStorage.getItem("admin_session_token") ||
          localStorage.getItem("admin_session_token");
        if (!token) {
          console.error("No session token for loading banks");
          return;
        }

        const banks = await BankService.getWebBankAccounts(token);
        console.log("🏦 [TransactionDialog] Loaded banks:", banks.length);
        console.log("🏦 [TransactionDialog] Raw banks:", banks);

        // Map to local WebBankAccount format
        const mappedBanks: WebBankAccount[] = banks.map((b) => ({
          id: b.id,
          bankName: b.bankName,
          bankAccount: b.bankAccount,
          accountName: b.name,
          imageUrl: b.imageUrl || "",
          amount: b.amount || 0,
          type: b.type as "deposit" | "withdraw",
          isBoth: b.isBoth,
          primaryDeposit: b.primaryDeposit,
          primaryWithdraw: b.primaryWithdraw,
          gatewayApi: b.gatewayApi, // ✅ เพิ่ม gatewayApi
        }));

        console.log("🏦 [TransactionDialog] Mapped banks:", mappedBanks);
        setBankAccounts(mappedBanks);
      } catch (error) {
        console.error("Error loading bank accounts:", error);
        toast.error("ไม่สามารถโหลดข้อมูลบัญชีธนาคาร");
      } finally {
        setIsLoadingBanks(false);
      }
    };

    loadBankAccounts();
  }, [open, sessionToken]);

  // ✅ ตั้งค่าเลือกธนาคารอัตโนมัติเมื่อโหลดเสร็จ (สำหรับ create mode)
  // - รายการฝาก → เลือก Primary Deposit Bank
  // - รายการถอน → เลือก Primary Withdraw Bank
  useEffect(() => {
    if (
      open &&
      isCreateMode &&
      !transaction &&
      availableBanks.length > 0 &&
      !selectedBankId
    ) {
      // ✅ หา Primary Bank ตาม mode และ member.bankType
      let primaryBank: WebBankAccount | undefined;

      if (isDeposit) {
        // ✅ ถ้า member เป็น TrueWallet (bank_type = 2) → เลือก Primary Deposit ที่เป็น TrueWallet
        if (selectedMember?.bankType === 2) {
          primaryBank = availableBanks.find(
            (b) => b.primaryDeposit && (b.banklistSid === 19 || b.bankId === 'truewallet')
          );
          console.log('🏦 [TransactionDialog] Member is TrueWallet, looking for TrueWallet primary deposit');
        }
        // ✅ ถ้า member เป็นธนาคารปกติ (bank_type = 1 หรือ undefined) → เลือก Primary Deposit ที่ไม่ใช่ TrueWallet
        else {
          primaryBank = availableBanks.find(
            (b) => b.primaryDeposit && b.banklistSid !== 19 && b.bankId !== 'truewallet'
          );
          console.log('🏦 [TransactionDialog] Member is regular bank, looking for regular primary deposit');
        }
      } else if (isWithdraw) {
        // ✅ ถอน: เลือก Primary Withdraw ที่ไม่ใช่ TrueWallet ก่อน (bank_type 1 > bank_type 2)
        primaryBank = availableBanks.find(
          (b) => b.primaryWithdraw && b.banklistSid !== 19 && b.bankId !== "truewallet"
        );
        if (!primaryBank) {
          primaryBank = availableBanks.find((b) => b.primaryWithdraw);
        }
      }

      // ถ้าไม่มี primary bank ให้เลือกตัวแรก
      const bankToSelect = primaryBank || availableBanks[0];

      setSelectedBankId(bankToSelect.id);
      console.log(
        `🏦 [TransactionDialog] Auto-selected ${isDeposit ? 'Primary Deposit' : 'Primary Withdraw'} bank:`,
        bankToSelect.bankName,
        primaryBank ? "(Primary)" : "(First available)"
      );
    }
  }, [open, isCreateMode, transaction, availableBanks, selectedBankId, isDeposit, isWithdraw, selectedMember]);

  // Initialize form with transaction data
  useEffect(() => {
    if (open) {
      if (transaction) {
        setIsBonus(transaction.isBonus || false);
        setSelectedBankId(transaction.webBankId || "");
        setSelectedMember({
          id: transaction.memberId || "",
          username: transaction.memberUsername || "",
          firstName: transaction.memberName?.split(" ")[0] || "",
          lastName: transaction.memberName?.split(" ")[1] || "",
          phone: "",
          bankAccount: transaction.memberBankAccount || "",
          bankName: transaction.memberBankName || "",
          agentStatus:
            (transaction.memberAgentStatus as "ACTIVE" | "SUSPEND") || "ACTIVE",
        });
        setDateTime(transaction.dateTime || "");
        setAmount(transaction.amount?.toString() || "");
        setStatus(transaction.status || "Pending");
        setNotes(transaction.notes || "");
        setPromotionCode(transaction.promotionCode || "");
        setImagePreview(transaction.image || null);
      } else {
        // Reset form for create mode
        setIsBonus(false);
        setSelectedBankId(""); // จะถูกตั้งค่าอัตโนมัติเมื่อโหลด bank accounts เสร็จ
        setSelectedMember(null);
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        setDateTime(now.toISOString().slice(0, 16));
        setAmount("");
        setStatus("Pending");
        setNotes("");
        setPromotionCode("");
        setImagePreview(null);
        setImageFile(null);
        setMemberPromotionInfo(null); // ✅ Reset promotion info
      }
    }
  }, [open, transaction]);

  // Search members - ค้นหาสมาชิกแบบ realtime จาก API
  const searchMembers = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) {
        setMembers([]);
        return;
      }

      setIsSearchingMembers(true);
      try {
        // 🔗 Call real API: getCustomers Cloud Function
        const userService = await getUserService();

        // Get session token from auth context or storage
        const token =
          sessionToken ||
          sessionStorage.getItem("admin_session_token") ||
          localStorage.getItem("admin_session_token");

        if (!token) {
          console.error("No session token found");
          setMembers([]);
          return;
        }

        const result = await userService.getMembers(token, {
          search: query, // canonical search
          page: 1,
          limit: 10,
          orderBy: "desc",
          orderByField: "createdAt",
        });

        console.log("🔍 [TransactionDialog] Search members result:", {
          query,
          total: result.total,
          itemsCount: result.items?.length,
        });

        // Map Member to MemberOption
        const memberOptions: MemberOption[] = (result.items || []).map(
          (m: Member) => ({
            id: m.id,
            username: m.username || m.phone, // agent_username หรือใช้ phone
            firstName: m.firstName,
            lastName: m.lastName,
            phone: m.phone,
            bankAccount: m.bankAccount,
            bankName: m.bankName,
            agentStatus: m.status === "Active" ? "ACTIVE" : "SUSPEND",
          })
        );

        setMembers(memberOptions);
      } catch (error) {
        console.error("Error searching members:", error);
        toast.error("เกิดข้อผิดพลาดในการค้นหาสมาชิก");
        setMembers([]);
      } finally {
        setIsSearchingMembers(false);
      }
    },
    [sessionToken]
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchMembers(memberSearchQuery);
    }, 300);
    return () => clearTimeout(debounce);
  }, [memberSearchQuery, searchMembers]);

  // Handle image upload - ซ่อนไว้ก่อน (ยังไม่ใช้)
  // const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     setImageFile(file);
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       setImagePreview(reader.result as string);
  //     };
  //     reader.readAsDataURL(file);
  //   }
  // };

  // Handle save
  const handleSave = async () => {
    if (!selectedMember || !amount || !dateTime) {
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmSave = async () => {
    setIsSaving(true);
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");

      if (!token) {
        toast.error("ไม่พบ Session Token กรุณาเข้าสู่ระบบใหม่");
        return;
      }

      // ✅ Duplicate Detection: ตรวจสอบรายการซ้ำก่อนสร้าง (เฉพาะ create mode + deposit)
      if (isCreateMode && isDeposit && selectedMember && amount && dateTime) {
        try {
          const transactionDate = dateTime.split('T')[0]; // YYYY-MM-DD
          const transactionAmount = parseFloat(amount);

          console.log('🔍 [Duplicate Check] Checking for duplicates:', {
            memberId: selectedMember.id,
            date: transactionDate,
            amount: transactionAmount
          });

          // เรียก API เพื่อตรวจสอบรายการซ้ำ
          const duplicateCheck = await TransactionService.checkDuplicate(token, {
            memberId: selectedMember.id,
            date: transactionDate,
            amount: transactionAmount,
            type: 'Deposit'
          });

          if (duplicateCheck.hasDuplicate) {
            const duplicateInfo = duplicateCheck.duplicates || [];
            const duplicateTypes = duplicateInfo.map((d: any) => d.source).join(', ');

            toast.warning('⚠️ พบรายการฝากที่คล้ายกัน', {
              description: `พบ ${duplicateInfo.length} รายการ (${duplicateTypes}) ในวันเดียวกัน จำนวนเงินเท่ากัน`,
              duration: 8000
            });

            // แสดง confirm dialog เพิ่มเติม
            const shouldContinue = window.confirm(
              `⚠️ พบรายการซ้ำ!\n\n` +
              `สมาชิก: ${selectedMember.firstName} ${selectedMember.lastName}\n` +
              `วันที่: ${transactionDate}\n` +
              `จำนวนเงิน: ${transactionAmount.toLocaleString()} บาท\n\n` +
              `พบรายการที่คล้ายกัน ${duplicateInfo.length} รายการ:\n` +
              duplicateInfo.map((d: any, i: number) =>
                `${i + 1}. ${d.source} - ${d.status} (${d.dateTime})`
              ).join('\n') +
              `\n\nต้องการสร้างรายการต่อหรือไม่?`
            );

            if (!shouldContinue) {
              setIsSaving(false);
              setShowConfirmDialog(false);
              return;
            }
          }
        } catch (error) {
          console.error('❌ [Duplicate Check] Error:', error);
          // ถ้า API check duplicate ไม่สำเร็จ ให้ดำเนินการต่อได้ (ไม่บล็อก)
          toast.warning('ไม่สามารถตรวจสอบรายการซ้ำได้ แต่จะดำเนินการต่อ');
        }
      }

      if (isEditMode && transaction) {
        // Update existing transaction
        console.log(
          "📝 [TransactionDialog] Updating transaction:",
          transaction.id
        );

        // ✅ ถ้าเลือก member ใหม่ → update member_id ด้วย
        const updateData: any = {
          status,
          notes,
          statusNote: notes, // Use notes as statusNote for now
        };

        if (selectedMember && selectedMember.id !== transaction.memberId) {
          updateData.memberId = selectedMember.id;
          console.log(
            "📝 [TransactionDialog] Updating member_id:",
            selectedMember.id
          );
        }

        await TransactionService.update(token, transaction.id, updateData);

        console.log("✅ [TransactionDialog] Transaction updated");
        toast.success("บันทึกการเปลี่ยนแปลงสำเร็จ");
      } else {
        // Create new transaction
        const createData: CreateTransactionData = {
          type: isDeposit ? "Deposit" : "Withdraw",
          isBonus,
          memberId: selectedMember!.id,
          webbankaccountId: isBonus ? null : selectedBankId,
          amount: parseFloat(amount),
          status: "Pending", // ✅ V2: สร้างเป็น Pending เสมอ (Deposit V2 จะใช้ createManualDepositV2)
          notes,
          dateTime,
          // ✅ V2: ไม่ส่ง withdrawBy ตอนสร้าง (จะเลือกตอนอนุมัติ)
          // withdrawBy: undefined,
          promotionCode: promotionCode || undefined,
          image: imageFile || imagePreview,
        };

        console.log("📝 [TransactionDialog] Creating transaction:", createData);

        const result = await TransactionService.create(token, createData);

        console.log("✅ [TransactionDialog] Transaction created:", result);
        console.log(
          "✅ [TransactionDialog] Transaction status:",
          result.status
        );
        toast.success(
          `สร้างรายการ${isDeposit ? "ฝาก" : "ถอน"}สำเร็จ (สถานะ: ${
            result.status || "Pending"
          })`
        );
      }

      // เรียก callback ถ้ามี
      if (onSave) {
        const formData: TransactionFormData = {
          type: isDeposit ? "Deposit" : "Withdraw",
          isBonus,
          webbankaccountId: isBonus ? null : selectedBankId,
          memberId: selectedMember!.id,
          memberName: `${selectedMember!.firstName} ${
            selectedMember!.lastName
          }`,
          memberUsername: selectedMember!.username,
          dateTime,
          amount: parseFloat(amount),
          status,
          notes,
          image: imageFile || imagePreview,
          withdrawBy: isWithdraw ? "Auto" : undefined,
          promotionCode: promotionCode || undefined,
        };
        await onSave(formData);
      }

      onOpenChange(false);
    } catch (error) {
      console.error("❌ [TransactionDialog] Error saving transaction:", error);
      toast.error(`เกิดข้อผิดพลาด: ${(error as Error).message}`);
    } finally {
      setIsSaving(false);
      setShowConfirmDialog(false);
    }
  };

  // For view/edit mode: look up from ALL banks (not filtered by type)
  // This fixes: linked bank has type="deposit" but transaction is a withdraw
  const selectedBank = (!isCreateMode && transaction)
    ? bankAccounts.find((b) => b.id === selectedBankId)
    : availableBanks.find((b) => b.id === selectedBankId);

  const getStatusBadge = (s: string) => {
    // ✅ Completed / Approved - สีเขียว
    if (s === "Completed" || s === "Approved") {
      return (
        <Badge className="bg-emerald-500 text-white">
          <CheckCircle className="w-3 h-3 mr-1" />
          {s === "Completed" ? "สำเร็จ" : "อนุมัติ"}
        </Badge>
      );
    }
    // ✅ Rejected - สีแดง
    if (s === "Rejected") {
      return (
        <Badge className="bg-red-500 text-white">
          <XCircle className="w-3 h-3 mr-1" />
          ปฏิเสธ
        </Badge>
      );
    }
    // ✅ Processing - สีน้ำเงิน
    if (s === "Processing") {
      return (
        <Badge className="bg-blue-500 text-white">
          <Clock className="w-3 h-3 mr-1" />
          กำลังดำเนินการ
        </Badge>
      );
    }
    // ✅ Cancelled - สีเทา
    if (s === "Cancelled") {
      return (
        <Badge className="bg-gray-500 text-white">
          <XCircle className="w-3 h-3 mr-1" />
          ยกเลิก
        </Badge>
      );
    }
    // ✅ Pending - สีเหลือง (default)
    return (
      <Badge className="bg-amber-500 text-white">
        <Clock className="w-3 h-3 mr-1" />
        รอดำเนินการ
      </Badge>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden">
          {/* Header */}
          <DialogHeader
            className={cn(
              "px-6 py-4 border-b",
              isDeposit
                ? "bg-emerald-50 dark:bg-emerald-950/30"
                : "bg-orange-50 dark:bg-orange-950/30"
            )}
          >
            <DialogTitle className="flex items-center gap-3">
              {isDeposit ? (
                <div className="p-2 rounded-full bg-emerald-500 text-white">
                  <ArrowDownToLine className="w-5 h-5" />
                </div>
              ) : (
                <div className="p-2 rounded-full bg-orange-500 text-white">
                  <ArrowUpFromLine className="w-5 h-5" />
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold">
                  {isCreateMode
                    ? isDeposit
                      ? "สร้างรายการฝาก"
                      : "สร้างรายการถอน"
                    : isDeposit
                    ? "รายละเอียดการฝาก"
                    : "รายละเอียดการถอน"}
                </h2>
                {transaction && (
                  <p className="text-sm text-muted-foreground">
                    #{transaction.id}
                  </p>
                )}
              </div>
              {transaction && (
                <div className="ml-auto">
                  {getStatusBadge(transaction.status || "Pending")}
                </div>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Content */}
          <ScrollArea className="max-h-[calc(90vh-180px)]">
            <div className="p-6 space-y-6">

              {/* Transfer Flow - แสดงเฉพาะ view/edit mode ที่มี transaction */}
              {transaction && !isCreateMode && (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                  {/* ต้นทาง */}
                  <div className={cn(
                    "p-3 rounded-xl border-2",
                    isDeposit
                      ? "border-blue-500/30 bg-blue-500/5"
                      : "border-orange-500/30 bg-orange-500/5"
                  )}>
                    <p className={cn(
                      "text-xs font-medium mb-2",
                      isDeposit ? "text-blue-600" : "text-orange-600"
                    )}>
                      บัญชีต้นทาง
                    </p>
                    <div className="flex items-center gap-2">
                      <BankLogo
                        bankValue={isDeposit ? transaction.memberBankName : transaction.webBankName}
                        size="lg"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono font-bold text-sm">
                          {isDeposit
                            ? transaction.memberBankAccount || '-'
                            : transaction.webBankAccount || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {isDeposit ? transaction.memberName : (transaction.webBankAccountName || transaction.webBankName || '-')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="hidden md:flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>

                  {/* ปลายทาง */}
                  <div className={cn(
                    "p-3 rounded-xl border-2",
                    isDeposit
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-blue-500/30 bg-blue-500/5"
                  )}>
                    <p className={cn(
                      "text-xs font-medium mb-2",
                      isDeposit ? "text-emerald-600" : "text-blue-600"
                    )}>
                      บัญชีปลายทาง
                    </p>
                    <div className="flex items-center gap-2">
                      <BankLogo
                        bankValue={isDeposit ? transaction.webBankName : transaction.memberBankName}
                        size="lg"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono font-bold text-sm">
                          {isDeposit
                            ? transaction.webBankAccount || '-'
                            : transaction.memberBankAccount || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {isDeposit ? (transaction.webBankAccountName || transaction.webBankName || '-') : (transaction.memberName || '-')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bonus Checkbox */}
              {isDeposit && (
                <div
                  className={cn(
                    "flex items-center gap-3 p-4 rounded-lg border-2 transition-colors",
                    isBonus
                      ? "border-purple-500 bg-purple-50 dark:bg-purple-950/20"
                      : "border-border bg-muted/30"
                  )}
                >
                  <Checkbox
                    id="isBonus"
                    checked={isBonus}
                    onCheckedChange={(checked) => setIsBonus(!!checked)}
                    disabled={isLocked || isUnidentifiedDepositEdit}
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="isBonus"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Gift className="w-4 h-4 text-purple-600" />
                      <span className="font-medium">ฝากโบนัส</span>
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      เติมเครดิต{" "}
                      <span className="text-red-500 font-medium">*ไม่โอน</span>{" "}
                      ผ่านระบบออโต้
                    </p>
                    {/* ✅ แสดงข้อความเมื่อเป็นรายการที่ไม่สามารถระบุได้ */}
                    {isUnidentifiedDepositEdit && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        ⚠️ รายการที่ไม่สามารถระบุตัวตนได้
                        ไม่สามารถใช้ฝากโบนัสได้
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Bank Account Selection */}
              {!isBonus && !isUnidentifiedDepositEdit && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    บัญชีธนาคาร {isDeposit ? "รับเงิน" : "โอนออก"}
                    {isLoadingBanks && (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    )}
                  </Label>
                  <Select
                    value={selectedBankId}
                    onValueChange={setSelectedBankId}
                    disabled={isLocked || isLoadingBanks}
                  >
                    <SelectTrigger className="h-auto py-3">
                      <SelectValue
                        placeholder={
                          isLoadingBanks ? "กำลังโหลด..." : "เลือกบัญชีธนาคาร"
                        }
                      >
                        {selectedBank ? (
                          <div className="flex items-center gap-3">
                            <BankLogo
                              bankValue={selectedBank.bankName}
                              size="md"
                            />
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <div className="font-medium">
                                  {selectedBank.bankName}
                                </div>
                                {selectedBank.gatewayApi && (
                                  <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-[10px] px-1.5 py-0">
                                    Gateway API
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {selectedBank.bankAccount} (
                                {formatCurrency(selectedBank.amount)})
                              </div>
                            </div>
                          </div>
                        ) : (!isCreateMode && transaction && (transaction.webBankName || transaction.webBankAccount)) ? (
                          // Fallback: show snapshot data when bank not found in list
                          <div className="flex items-center gap-3">
                            <BankLogo bankValue={transaction.webBankName} size="md" />
                            <div className="text-left">
                              <div className="font-medium">{transaction.webBankName || '-'}</div>
                              <div className="text-xs text-muted-foreground">{transaction.webBankAccount || '-'}</div>
                            </div>
                          </div>
                        ) : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availableBanks.length === 0 && !isLoadingBanks && (
                        <div className="py-4 text-center text-sm text-muted-foreground">
                          ไม่พบบัญชีธนาคาร
                        </div>
                      )}
                      {availableBanks.map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          <div className="flex items-center gap-3">
                            <BankLogo bankValue={bank.bankName} size="md" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="font-medium">
                                  {bank.bankName}
                                </div>
                                {bank.gatewayApi && (
                                  <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-[10px] px-1.5 py-0">
                                    Gateway API
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                บ.ช. {bank.bankAccount} (
                                {formatCurrency(bank.amount)})
                              </div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* ✅ สำหรับรายการฝากที่ "ไม่สามารถระบุได้": แสดงข้อมูลธนาคารแบบ read-only */}
              {isUnidentifiedDepositEdit && transaction?.webBankId && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    บัญชีธนาคารรับเงิน
                  </Label>
                  <div className="p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3">
                      {selectedBank && (
                        <>
                          <BankLogo
                            bankValue={selectedBank.bankName}
                            size="md"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium">
                                {selectedBank.bankName}
                              </div>
                              {selectedBank.gatewayApi && (
                                <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-[10px] px-1.5 py-0">
                                  Gateway API
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {selectedBank.bankAccount} (
                              {formatCurrency(selectedBank.amount)})
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                      ⚠️ รายการที่ไม่สามารถระบุตัวตนได้:
                      ไม่สามารถแก้ไขบัญชีธนาคารได้
                    </p>
                  </div>
                </div>
              )}

              {/* Member Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  สมาชิก <span className="text-red-500">*</span>
                </Label>

                {isLocked && selectedMember ? (
                  // View mode - show member info
                  <div className="p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold",
                          selectedMember.agentStatus === "ACTIVE"
                            ? "bg-emerald-500"
                            : "bg-red-500"
                        )}
                      >
                        {selectedMember.username.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {selectedMember.username}
                          </span>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px]",
                              selectedMember.agentStatus === "ACTIVE"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-red-50 text-red-700 border-red-200"
                            )}
                          >
                            {selectedMember.agentStatus}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {selectedMember.firstName} {selectedMember.lastName}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1">
                            <Smartphone className="w-3 h-3" />
                            {selectedMember.phone || "-"}
                          </span>
                          <span className="flex items-center gap-1">
                            <BankLogo
                              bankValue={selectedMember.bankName}
                              size="xs"
                            />
                            {selectedMember.bankAccount}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Create/Edit mode - searchable dropdown
                  <Popover
                    open={memberSearchOpen}
                    onOpenChange={setMemberSearchOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className={cn(
                          "w-full justify-between h-auto py-3",
                          !selectedMember && "text-muted-foreground"
                        )}
                        disabled={isLocked}
                      >
                        {selectedMember ? (
                          <div className="flex items-center gap-3 text-left">
                            <div
                              className={cn(
                                "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold",
                                selectedMember.agentStatus === "ACTIVE"
                                  ? "bg-emerald-500"
                                  : "bg-red-500"
                              )}
                            >
                              {selectedMember.username
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium flex items-center gap-2">
                                {selectedMember.username}
                                <span
                                  className={cn(
                                    "w-2 h-2 rounded-full",
                                    selectedMember.agentStatus === "ACTIVE"
                                      ? "bg-emerald-500"
                                      : "bg-red-500"
                                  )}
                                />
                              </div>
                              <div className="text-xs text-muted-foreground flex items-center gap-2">
                                <span>{selectedMember.firstName}</span>
                                <span>📲 {selectedMember.phone}</span>
                                <BankLogo
                                  bankValue={selectedMember.bankName}
                                  size="xs"
                                />
                                <span>{selectedMember.bankAccount}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Search className="w-4 h-4" />
                            ค้นหา: ยูสเซอร์, ชื่อ, เบอร์โทร, เลขบัญชี
                          </span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[--radix-popover-trigger-width] min-w-[400px] p-0"
                      align="start"
                    >
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="พิมพ์เพื่อค้นหา..."
                          value={memberSearchQuery}
                          onValueChange={setMemberSearchQuery}
                        />
                        <CommandList>
                          {isSearchingMembers && (
                            <div className="flex items-center justify-center py-6">
                              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                            </div>
                          )}
                          {!isSearchingMembers &&
                            members.length === 0 &&
                            memberSearchQuery.length >= 2 && (
                              <CommandEmpty>ไม่พบสมาชิก</CommandEmpty>
                            )}
                          {!isSearchingMembers &&
                            memberSearchQuery.length < 2 && (
                              <div className="py-6 text-center text-sm text-muted-foreground">
                                พิมพ์อย่างน้อย 2 ตัวอักษร
                              </div>
                            )}
                          <CommandGroup>
                            {members.map((member) => (
                              <CommandItem
                                key={member.id}
                                value={member.id}
                                onSelect={async () => {
                                  setSelectedMember(member);
                                  setMemberSearchOpen(false);
                                  setMemberSearchQuery("");

                                  // ✅ V2: ตรวจสอบ Promotion Info เมื่อเลือกสมาชิก (สำหรับ Withdraw)
                                  if (isWithdraw) {
                                    try {
                                      const userService =
                                        await getUserService();
                                      const token =
                                        sessionToken ||
                                        sessionStorage.getItem(
                                          "admin_session_token"
                                        ) ||
                                        localStorage.getItem(
                                          "admin_session_token"
                                        );

                                      if (token) {
                                        const memberDetail =
                                          await userService.getMember(
                                            token,
                                            member.id
                                          );

                                        // ใช้ promotion field จาก Member type
                                        const promotion =
                                          memberDetail.promotion;
                                        const maxWithdraw =
                                          promotion?.max_withdraw ||
                                          promotion?.maxWithdraw ||
                                          0;

                                        if (maxWithdraw > 0) {
                                          setMemberPromotionInfo({
                                            code:
                                              promotion?.promotion_code ||
                                              promotion?.code ||
                                              "",
                                            max_withdraw: maxWithdraw,
                                            hasPromotion: true,
                                          });
                                        } else {
                                          setMemberPromotionInfo({
                                            hasPromotion: false,
                                          });
                                        }
                                      }
                                    } catch (error) {
                                      console.error(
                                        "Error loading promotion info:",
                                        error
                                      );
                                      setMemberPromotionInfo(null);
                                    }
                                  }
                                }}
                                className="py-3"
                              >
                                <div className="flex items-center gap-3 w-full">
                                  <div
                                    className={cn(
                                      "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold",
                                      member.agentStatus === "ACTIVE"
                                        ? "bg-emerald-500"
                                        : "bg-red-500"
                                    )}
                                  >
                                    {member.username.slice(0, 2).toUpperCase()}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium flex items-center gap-2">
                                      {member.username}
                                      <span
                                        className={cn(
                                          "w-2 h-2 rounded-full",
                                          member.agentStatus === "ACTIVE"
                                            ? "bg-emerald-500"
                                            : "bg-red-500"
                                        )}
                                      />
                                    </div>
                                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                                      <span>
                                        {member.firstName} {member.lastName}
                                      </span>
                                      <span>📲 {member.phone}</span>
                                      <BankLogo
                                        bankValue={member.bankName}
                                        size="xs"
                                      />
                                      <span>{member.bankAccount}</span>
                                    </div>
                                  </div>
                                  {selectedMember?.id === member.id && (
                                    <Check className="w-4 h-4 text-primary" />
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}

                {selectedMember && !isLocked && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => setSelectedMember(null)}
                  >
                    <X className="w-3 h-3 mr-1" />
                    ล้างการเลือก
                  </Button>
                )}
              </div>

              {/* Date Time - Custom Picker (Thai Format + 24-hour) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  วันที่ <span className="text-red-500">*</span>
                </Label>
                <div className="grid grid-cols-2 gap-4 max-w-md">
                  {/* Date Picker - Thai Style */}
                  <ThaiDatePicker
                    selected={dateTime ? new Date(dateTime) : null}
                    onChange={(date) => {
                      if (date) {
                        // Preserve time from current dateTime
                        const existingTime = dateTime?.split('T')[1] || '00:00';
                        const dateStr = toDateInputValue(date); // YYYY-MM-DD
                        setDateTime(`${dateStr}T${existingTime}`);
                      }
                    }}
                    disabled={isLocked || isUnidentifiedDepositEdit}
                    placeholder="เลือกวันที่"
                    locale="th"
                  />

                  {/* Time Picker - 24 Hour */}
                  <TimePicker24
                    selected={dateTime ? new Date(`2000-01-01T${dateTime.split('T')[1] || '00:00'}`) : null}
                    onChange={(time) => {
                      if (time) {
                        // Preserve date from current dateTime
                        const existingDate = dateTime?.split('T')[0] || toDateInputValue(new Date());
                        const timeStr = toTimeInputValue(time); // HH:mm
                        setDateTime(`${existingDate}T${timeStr}`);
                      }
                    }}
                    disabled={isLocked || isUnidentifiedDepositEdit}
                    placeholder="เลือกเวลา"
                  />
                </div>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  จำนวนเงิน <span className="text-red-500">*</span>
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={isLocked || isUnidentifiedDepositEdit}
                    className="max-w-xs text-lg font-bold"
                  />
                  <span className="text-muted-foreground">บาท</span>
                </div>
                {amount && (
                  <p
                    className={cn(
                      "text-2xl font-bold",
                      isDeposit ? "text-emerald-600" : "text-orange-600"
                    )}
                  >
                    {formatCurrency(parseFloat(amount) || 0)}
                  </p>
                )}

                {/* ✅ V2: แสดง Promotion Info สำหรับ Withdraw */}
                {isWithdraw &&
                  isCreateMode &&
                  memberPromotionInfo?.hasPromotion &&
                  amount && (
                    <div className="mt-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">
                          ⚠️ สมาชิกมีโปรโมชัน: {memberPromotionInfo.code}
                        </span>
                      </div>
                      <div className="text-xs text-yellow-700 dark:text-yellow-400 space-y-1">
                        <div className="flex justify-between">
                          <span>ถอนได้สูงสุด:</span>
                          <span className="font-mono font-semibold">
                            {memberPromotionInfo.max_withdraw?.toLocaleString()}{" "}
                            บาท
                          </span>
                        </div>
                        {parseFloat(amount) >
                          (memberPromotionInfo.max_withdraw || 0) && (
                          <div className="pt-1 border-t border-yellow-300 dark:border-yellow-700">
                            <p className="text-yellow-900 dark:text-yellow-200 font-semibold">
                              ⚠️ จำนวนเงินที่กรอก (
                              {parseFloat(amount).toLocaleString()} บาท)
                              เกินเงื่อนไขโปรโมชัน (
                              {memberPromotionInfo.max_withdraw?.toLocaleString()}{" "}
                              บาท)
                            </p>
                            <p className="text-yellow-800 dark:text-yellow-300 mt-1">
                              💡 ระบบจะตัดเครดิตทั้งหมด แต่จะถอนได้ตาม
                              max_withdraw เท่านั้น
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>

              {/* ✅ V2: แสดงข้อมูล Flow สำหรับ Deposit V2 */}
              {isDeposit && isCreateMode && (
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                    <div className="flex-1 text-sm text-emerald-700 dark:text-emerald-300">
                      <p className="font-semibold mb-1">
                        📋 ข้อมูลสำหรับ Deposit V2 Flow
                      </p>
                      <ul className="space-y-1 text-xs">
                        <li>
                          • รายการจะถูกสร้างเป็นสถานะ{" "}
                          <span className="font-semibold">"Pending"</span> เสมอ
                        </li>
                        <li>
                          • Admin จะต้อง{" "}
                          <span className="font-semibold">
                            "ยืนยันเงินเข้า"
                          </span>{" "}
                          ก่อน (Payment Status)
                        </li>
                        <li>
                          • จากนั้น{" "}
                          <span className="font-semibold">"เติมเครดิต"</span>{" "}
                          เข้าเกม (Credit Status)
                        </li>
                        <li>
                          • เมื่อทั้งสองขั้นตอนสำเร็จ →{" "}
                          <span className="font-semibold">"Completed"</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* ✅ V2: แสดงข้อมูลสำหรับ Withdraw */}
              {isWithdraw && isCreateMode && (
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="flex-1 text-sm text-blue-700 dark:text-blue-300">
                      <p className="font-semibold mb-1">
                        📋 ข้อมูลสำหรับ Flow V2
                      </p>
                      <ul className="space-y-1 text-xs">
                        <li>
                          • รายการจะถูกสร้างเป็นสถานะ{" "}
                          <span className="font-semibold">"Pending"</span>
                        </li>
                        <li>
                          • Admin จะเลือก{" "}
                          <span className="font-semibold">
                            "อนุมัติ Manual"
                          </span>{" "}
                          หรือ{" "}
                          <span className="font-semibold">"อนุมัติ Auto"</span>{" "}
                          ในตาราง
                        </li>
                        {memberPromotionInfo?.hasPromotion && (
                          <li>
                            • ⚠️ สมาชิกมีโปรโมชัน: ระบบจะตัดเครดิตทั้งหมด
                            แต่ถอนได้ตาม max_withdraw
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Promotion Code (Deposit only) */}
              {isDeposit && isBonus && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    รหัสโปรโมชั่น
                  </Label>
                  <Input
                    placeholder="กรอกรหัสโปรโมชั่น (ถ้ามี)"
                    value={promotionCode}
                    onChange={(e) => setPromotionCode(e.target.value)}
                    disabled={isLocked}
                    className="max-w-xs"
                  />
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  หมายเหตุ
                </Label>
                <Textarea
                  placeholder="บันทึกหมายเหตุ..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={isLocked}
                  rows={3}
                />
              </div>

              {/* Image Upload - ซ่อนไว้ก่อน (ยังไม่ใช้) */}
              {/* 
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  รูปสลิป
                </Label>
                <div className="flex items-start gap-4">
                  <div className="w-32 h-32 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden bg-muted/30">
                    {imagePreview ? (
                      <img
                        src={imagePreview || ""}
                        alt="Slip"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center text-muted-foreground">
                        <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                        <span className="text-xs">ไม่มีรูป</span>
                      </div>
                    )}
                  </div>
                  {!isViewMode && (
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleImageUpload}
                        className="max-w-xs"
                      />
                      {imagePreview && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500"
                          onClick={() => {
                            setImagePreview(null);
                            setImageFile(null);
                          }}
                        >
                          <X className="w-3 h-3 mr-1" />
                          ลบรูป
                        </Button>
                      )}
                    </div>
                  )}
                  {isViewMode && imagePreview && (
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={imagePreview || ""}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        ดูรูปขนาดเต็ม
                      </a>
                    </Button>
                  )}
                </div>
              </div>
              */}

              {/* Withdraw Status Info */}
              {isWithdraw && transaction && (
                <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    สถานะการถอน
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        สถานะเครดิต:
                      </span>
                      <Badge variant="outline" className="ml-2">
                        {transaction.isCreditWithdraw === "1"
                          ? "ตัดแล้ว"
                          : transaction.isCreditWithdraw === "2"
                          ? "กำลังตัด"
                          : transaction.isCreditWithdraw === "3"
                          ? "ไม่สำเร็จ"
                          : "รอตัด"}
                      </Badge>
                      {transaction.isCreditWithdrawAmount && (
                        <span className="ml-2 text-emerald-600 font-medium">
                          {transaction.isCreditWithdrawAmount.toLocaleString()}{" "}
                          บาท
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">สถานะเงิน:</span>
                      <Badge variant="outline" className="ml-2">
                        {transaction.isCashWithdraw === "1"
                          ? "โอนแล้ว"
                          : transaction.isCashWithdraw === "2"
                          ? "กำลังโอน"
                          : transaction.isCashWithdraw === "3"
                          ? "ไม่สำเร็จ"
                          : "รอโอน"}
                      </Badge>
                      {transaction.isCashWithdrawAmount && (
                        <span className="ml-2 text-emerald-600 font-medium">
                          {transaction.isCashWithdrawAmount.toLocaleString()}{" "}
                          บาท
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">วิธีถอน:</span>
                      <Badge variant="outline" className="ml-2">
                        {transaction.withdrawBy === "Manaul" ||
                        transaction.withdrawBy === "Manual"
                          ? "Manual"
                          : "Auto"}
                      </Badge>
                    </div>
                    {(transaction as any).apiBankObj?.status && (
                      <div>
                        <span className="text-muted-foreground">
                          API Status:
                        </span>
                        <Badge
                          variant="outline"
                          className={cn(
                            "ml-2",
                            (transaction as any).apiBankObj.status ===
                              "success" &&
                              "bg-emerald-50 text-emerald-700 border-emerald-200",
                            (transaction as any).apiBankObj.status ===
                              "failed" &&
                              "bg-red-50 text-red-700 border-red-200",
                            (transaction as any).apiBankObj.status ===
                              "pending" &&
                              "bg-amber-50 text-amber-700 border-amber-200",
                            (transaction as any).apiBankObj.status ===
                              "processing" &&
                              "bg-blue-50 text-blue-700 border-blue-200"
                          )}
                          title={(transaction as any).apiBankObj.notes?.note}
                        >
                          {(transaction as any).apiBankObj.status}
                        </Badge>
                      </div>
                    )}
                  </div>
                  {transaction.uuid && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground text-xs">
                        UUID:{" "}
                      </span>
                      <span className="font-mono text-xs">
                        {transaction.uuid}
                      </span>
                    </div>
                  )}
                  {transaction.promotionCode && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground text-xs">
                        รหัสโปรโมชั่น:{" "}
                      </span>
                      <Badge
                        variant="outline"
                        className="bg-yellow-50 text-yellow-700 border-yellow-200"
                      >
                        {transaction.promotionCode}
                      </Badge>
                    </div>
                  )}
                </div>
              )}

              {/* Transfer Info (if createFrom === 'Transfer') */}
              {transaction?.createFrom === "Transfer" && (
                <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    ข้อมูลการโอน
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">จากบัญชี:</span>
                      <div className="mt-1 flex items-center gap-2">
                        <BankLogo
                          bankValue={(transaction as any).transferFromBankName}
                          size="xs"
                        />
                        <span className="font-mono">
                          {(transaction as any).transferFromBankAccount}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {(transaction as any).transferFromAccountName}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">ไปบัญชี:</span>
                      <div className="mt-1 flex items-center gap-2">
                        <BankLogo
                          bankValue={(transaction as any).transferToBankName}
                          size="xs"
                        />
                        <span className="font-mono">
                          {(transaction as any).transferToBankAccount}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {(transaction as any).transferToAccountName}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Deposit Status Note */}
              {isDeposit && transaction?.statusNote && (
                <div
                  className={cn(
                    "p-4 rounded-lg border text-sm",
                    transaction.statusNote.includes("ไม่พบ") &&
                      "bg-red-50 border-red-200 text-red-700",
                    transaction.statusNote.includes("ซ้ำ") &&
                      "bg-red-50 border-red-200 text-red-700",
                    transaction.statusNote.includes("รอ") &&
                      "bg-amber-50 border-amber-200 text-amber-700",
                    transaction.statusNote.includes("ดีเลย์") &&
                      "bg-purple-50 border-purple-200 text-purple-700",
                    !transaction.statusNote.includes("ไม่พบ") &&
                      !transaction.statusNote.includes("ซ้ำ") &&
                      !transaction.statusNote.includes("รอ") &&
                      !transaction.statusNote.includes("ดีเลย์") &&
                      "bg-muted/30"
                  )}
                >
                  <div className="font-medium mb-1">หมายเหตุสถานะ:</div>
                  <div>{transaction.statusNote}</div>
                  {(transaction as any).apiBankObj?.imagePath && (
                    <a
                      href={getSlipImageUrl((transaction as any).apiBankObj.imagePath) || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <ImageIcon className="w-4 h-4" />
                      ดู QR Code
                    </a>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-muted/30 flex items-center justify-between">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <X className="w-4 h-4 mr-2" />
              {isViewMode ? "ปิด" : "ยกเลิก"}
            </Button>

            {!isViewMode && (
              <Button
                onClick={handleSave}
                disabled={isSaving || !selectedMember || !amount || !dateTime}
                className={cn(
                  isDeposit
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-orange-600 hover:bg-orange-700"
                )}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isCreateMode ? "สร้างรายการ" : "บันทึก"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <SaveConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={confirmSave}
        title={
          isCreateMode ? "ยืนยันสร้างรายการ" : "ยืนยันบันทึกการเปลี่ยนแปลง"
        }
        description={
          isCreateMode
            ? `${isDeposit ? "ฝาก" : "ถอน"} จำนวน ${formatCurrency(
                parseFloat(amount) || 0
              )} ให้ ${selectedMember?.firstName} ${
                selectedMember?.lastName
              } (${selectedMember?.username})`
            : "ต้องการบันทึกการเปลี่ยนแปลงหรือไม่?"
        }
        changes={[]}
        isLoading={isSaving}
      />
    </>
  );
}

export default TransactionDialog;
